/** biome-ignore-all lint/a11y/noLabelWithoutControl: its ok */
"use client";
import { Checkbox } from "@base-ui/react/checkbox";
import { Dialog } from "@base-ui/react/dialog";
import { Field } from "@base-ui/react/field";
import { Fieldset } from "@base-ui/react/fieldset";
import { Form } from "@base-ui/react/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { CheckIcon } from "lucide-react";
import { useState } from "react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { submitAcademyForm } from "@/actions/academy.action";
import { Button } from "@/components/ui/button";
import {
  type AcademyFormModel,
  academyFormSchema,
} from "@/schemas/academy.schema";
import cities from "@/store/cities.json";
import type { FormField } from "@/types/academy";
import { FormFieldType } from "@/types/academy";
import FormInput from "./form-input";
import FormSelect from "./form-select";
import FormText from "./form-text";
import FormYesNo from "./form-yesorno";

const FormBlock = ({ block, index }: { block: FormField; index: number }) => {
  const name = `answers.${index}.value` as const;
  switch (block.fieldType) {
    case FormFieldType.FullName:
      return (
        <FormInput
          block={{ ...block, label: block.label || "Ad Soyad" }}
          name={name}
        />
      );
    case FormFieldType.Phone:
      return (
        <FormInput
          block={{ ...block, label: block.label || "Telefon" }}
          name={name}
        />
      );
    case FormFieldType.City:
      return (
        <FormSelect
          block={{
            ...block,
            label: block.label || "Şehir",
            optionItems: Object.entries(cities).map((city) => ({
              id: city[0],
              label: city[1],
            })),
          }}
          name={name}
        />
      );
    case FormFieldType.OpenText:
      return <FormText block={block} name={name} />;
    case FormFieldType.YesOrNo:
      return (
        <FormYesNo
          block={{ ...block, label: block.label || "Evet/Hayır" }}
          name={name}
        />
      );
    case FormFieldType.Dropdown:
      return <FormSelect block={block} name={name} />;
    default:
      return null;
  }
};

const FormBuilder = ({
  fields,
  consent,
  academyCampId,
}: {
  fields: FormField[];
  academyCampId: string;
  consent: {
    consentLinkText: string;
    consentPrefix?: string;
    consentText: string;
    consentUrl: string;
    submitButtonText: string;
  };
}) => {
  const sortedFields = [...fields].sort(
    (a, b) => a.displayOrder - b.displayOrder,
  );
  const form = useForm<AcademyFormModel>({
    defaultValues: {
      academyCampId,
      answers: sortedFields.map((f) => ({
        id: f.id,
        value: f.prefillValue || "",
      })),
      consent: false,
    },
    resolver: zodResolver(academyFormSchema),
  });
  useFieldArray({ control: form.control, name: "answers" });
  const [successOpen, setSuccessOpen] = useState(false);
  const mutation = useMutation({
    mutationFn: async (data: AcademyFormModel) => {
      return submitAcademyForm(data);
    },
    onSuccess: () => {
      form.reset();
      setSuccessOpen(true);
    },
  });
  const submitHandler = (data: AcademyFormModel) => {
    mutation.mutate(data);
  };
  return (
    <>
      <Dialog.Root onOpenChange={setSuccessOpen} open={successOpen}>
        <Dialog.Portal>
          <Dialog.Backdrop className="fixed inset-0 z-100 bg-black/40 transition-all duration-150 data-starting-style:opacity-0 data-ending-style:opacity-0" />
          <Dialog.Popup className="fixed z-100 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm bg-white rounded-2xl p-6 shadow-xl outline-none transition-all duration-150 data-starting-style:scale-90 data-starting-style:opacity-0 data-ending-style:scale-90 data-ending-style:opacity-0">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="flex items-center justify-center size-14 rounded-full bg-green-50">
                <CheckIcon className="size-7 stroke-green-500" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Dialog.Title className="text-lg font-semibold text-gray-900">
                  Başvurunuz Alındı
                </Dialog.Title>
                <Dialog.Description className="text-sm text-gray-500">
                  Kaydınız başarıyla alınmıştır.
                </Dialog.Description>
              </div>
              <Dialog.Close
                className="w-full py-2 text-sm font-medium text-gray-700 hover:text-gray-900 cursor-pointer"
                render={<button type="button" />}
              >
                Tamam
              </Dialog.Close>
            </div>
          </Dialog.Popup>
        </Dialog.Portal>
      </Dialog.Root>
      <FormProvider {...form}>
      <Form
        className="w-full md:w-2/3 flex flex-col gap-2 "
        onSubmit={form.handleSubmit(submitHandler)}
      >
        {sortedFields.map((field, index) => (
          <FormBlock block={field} index={index} key={field.id} />
        ))}
        <Controller
          control={form.control}
          defaultValue={false}
          name="consent"
          render={({ field, fieldState }) => {
            return (
              <Field.Root
                dirty={fieldState.isDirty}
                invalid={fieldState.invalid}
                touched={fieldState.isTouched}
              >
                <Fieldset.Root>
                  <label className="flex items-start gap-2 text-base text-gray-900 font-normal">
                    <Checkbox.Root
                      className="flex size-5 shrink-0 items-center justify-center rounded-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 data-[checked]:bg-gray-900 data-[unchecked]:border data-[unchecked]:border-gray-300"
                      defaultChecked={false}
                      onBlur={field.onBlur}
                      onCheckedChange={field.onChange}
                      ref={field.ref}
                      value={String(field.value)}
                    >
                      <Checkbox.Indicator className="flex text-gray-50 data-[unchecked]:hidden">
                        <CheckIcon className="size-3" />
                      </Checkbox.Indicator>
                    </Checkbox.Root>
                    <span>
                      {consent.consentPrefix}
                      <a
                        className="text-amber-500 underline"
                        href={consent.consentUrl}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        {consent.consentLinkText}
                      </a>
                      {consent.consentText}
                    </span>
                  </label>
                  <Field.Error
                    className="text-sm text-red-600 font-medium"
                    match={!!fieldState.error}
                  >
                    Bu alan zorunludur
                  </Field.Error>
                </Fieldset.Root>
              </Field.Root>
            );
          }}
          rules={{ required: true }}
        />
        <Button className="md:w-fit" type="submit">
          {consent.submitButtonText || "Başvur"}
        </Button>
      </Form>
    </FormProvider>
    </>
  );
};

export default FormBuilder;
